import React, { useState, useEffect } from 'react';
import { Star, User, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const DoctorReviews = ({ doctorId, totalReviews = 0 }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (expanded && reviews.length === 0) {
      fetchReviews();
    }
  }, [expanded]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reviews/${doctorId}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex space-x-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (totalReviews === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      <Button
        variant="ghost"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-sm text-gray-600 hover:text-teal-600"
        data-testid="toggle-reviews"
      >
        <span className="flex items-center">
          <MessageSquare className="w-4 h-4 mr-2" />
          {totalReviews} avis vérifié{totalReviews > 1 ? 's' : ''}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </Button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {loading ? (
            <div className="flex justify-center py-4">
              <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : reviews.length > 0 ? (
            reviews.map((review) => (
              <Card key={review.id} className="bg-gray-50" data-testid={`review-${review.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-teal-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{review.patient_name}</span>
                        <span className="text-xs text-gray-500">{formatDate(review.created_at)}</span>
                      </div>
                      {renderStars(review.rating)}
                      {review.comment && (
                        <p className="text-sm text-gray-600 mt-2">{review.comment}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <p className="text-sm text-gray-500 text-center py-2">Aucun avis pour le moment</p>
          )}
        </div>
      )}
    </div>
  );
};

export default DoctorReviews;
